import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { SettingsFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { ToggleProps } from "models/propTypes/wintFormPropsTypes";
import { BankBalanceSettingsFormProps } from "models/propTypes/settingsPropTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import {
  Description,
  GroupSection,
  HorizontalFormField,
  Label,
  Toggle,
  useWintForm,
  WintForm,
} from "components/common/wintForms";
import { Grid } from "@mui/material";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

/**
 * One show/hide toggle per bank account with a live ISO 20022 connection,
 * controlling which balances the dashboard widget shows.
 *
 * Built on wintForms rather than the deprecated `GenericForm` its sibling
 * settings forms use.
 */
const BankBalanceSettingsForm: ForwardRefRenderFunction<FormHandles, BankBalanceSettingsFormProps> = (props, ref) => {
  const { defaultValues, loadFailed, validationSchema, handleSubmit, onFormValidationChange, onFormDirtyChange } =
    props;

  const { wintFormProps, connect, formState } = useWintForm<SettingsFormTypes.BankBalanceSettings>({
    formRef: ref,
    defaultValues,
    validationSchema,
    handleSubmit,
    onFormValidationChange,
  });

  // useWintForm doesn't forward a dirty flag, which SettingsFormViewPanel needs.
  React.useEffect(() => {
    onFormDirtyChange && onFormDirtyChange(formState.dirty);
  }, [formState.dirty]);

  const accountLabel = (account: SettingsFormTypes.BankBalanceAccountSetting) => (
    <Grid container direction="column">
      <Label inline>{[account.accountNumber, account.bankName].filter(Boolean).join(" · ")}</Label>
      <Description horizontal>{account.bankAccountNumber}</Description>
    </Grid>
  );

  if (!defaultValues.accounts.length) {
    return (
      <Grid container>
        <Description>
          {LocalizationHelperInstance.getFormattedMessage(
            loadFailed
              ? TranslationKeys.Settings.BankBalancesCouldNotLoad
              : TranslationKeys.Settings.BankBalancesNoAccounts
          )}
        </Description>
      </Grid>
    );
  }

  return (
    <WintForm {...wintFormProps}>
      <GroupSection
        header={TranslationKeys.Settings.BankBalances}
        description={TranslationKeys.Settings.BankBalancesDescription}
        icon={IconDefinitionConstants.WBank}
      >
        {defaultValues.accounts.map((account, index) => (
          <HorizontalFormField<ToggleProps>
            key={account.accountId}
            Component={Toggle}
            {...connect(`accounts[${index}].showOnDashboard` as never)}
            label={accountLabel(account)}
            mainLabelGridSize={3}
            childGridSize={5}
            verticalAlignCenter
            className={cssExports.bankBalanceSettingsToggleRow}
          />
        ))}
      </GroupSection>
    </WintForm>
  );
};

export default forwardRef(BankBalanceSettingsForm);
